import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';
import webSocketService from '../services/websocket';
import toast from 'react-hot-toast';

const AuthContext = createContext();

const initialState = {
  user: null,
  token: null,
  loading: true,
  isAuthenticated: false
};

function authReducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };

    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false
      };

    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false
      };

    case 'UPDATE_USER':
      return {
        ...state,
        user: { ...state.user, ...action.payload }
      };

    default:
      return state;
  }
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state on mount
  useEffect(() => {
    initializeAuth();
  }, []);

  // Set up WebSocket connection when authenticated
  useEffect(() => {
    if (state.isAuthenticated && state.token) {
      try {
        // The connection will automatically subscribe in the onopen handler
        webSocketService.connect(state.token);
      } catch (error) {
        console.error('WebSocket connection failed:', error);
      }
    } else {
      webSocketService.disconnect();
    }
  }, [state.isAuthenticated, state.token]);

  const initializeAuth = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const userData = localStorage.getItem('user');

      if (token && userData) {
        // Verify token is still valid
        await authAPI.verifyToken();

        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: {
            token,
            user: JSON.parse(userData)
          }
        });
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      // Clear invalid tokens
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const sendMagicLink = async (email) => {
    try {
      const response = await authAPI.sendMagicLink({ email });

      toast.success('Magic link sent! Check your email.');
      return { success: true };

    } catch (error) {
      const message = error.response?.data?.error || error.response?.data?.message || 'Failed to send magic link';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const verifyMagicLink = useCallback(async (token) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      const response = await authAPI.verifyMagicLink({ token });
      const { user, token: authToken } = response.data;

      // Store in localStorage
      localStorage.setItem('authToken', authToken);
      localStorage.setItem('user', JSON.stringify(user));

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, token: authToken }
      });

      toast.success('Login successful!');
      return { success: true };

    } catch (error) {
      const message = error.response?.data?.error || error.response?.data?.message || 'Magic link verification failed';
      toast.error(message);

      dispatch({ type: 'SET_LOADING', payload: false });
      return { success: false, error: message };
    }
  }, []);

  const register = async (userData) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      const response = await authAPI.register(userData);

      // Registration now sends email with passcode - user needs to verify
      toast.success('Registration email sent! Please check your inbox for verification code.');

      dispatch({ type: 'SET_LOADING', payload: false });
      return {
        success: true,
        requiresVerification: true,
        email: userData.email
      };

    } catch (error) {
      const message = error.response?.data?.error || error.response?.data?.message || 'Registration failed';
      toast.error(message);

      dispatch({ type: 'SET_LOADING', payload: false });
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint (optional)
      await authAPI.logout();
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      // Always clear local state
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');

      // Disconnect WebSocket
      webSocketService.disconnect();

      dispatch({ type: 'LOGOUT' });
      toast.success('Logged out successfully');
    }
  };

  const updateUser = (updates) => {
    const updatedUser = { ...state.user, ...updates };
    localStorage.setItem('user', JSON.stringify(updatedUser));

    dispatch({
      type: 'UPDATE_USER',
      payload: updates
    });
  };

  const refreshToken = async () => {
    try {
      const response = await authAPI.refresh();
      const { token } = response.data;

      localStorage.setItem('authToken', token);

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: state.user,
          token
        }
      });

      return token;
    } catch (error) {
      console.error('Token refresh failed:', error.message);
      logout();
      throw error;
    }
  };

  const value = {
    ...state,
    sendMagicLink,
    verifyMagicLink,
    register,
    logout,
    updateUser,
    refreshToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
